import { createContext, useContext } from 'react';

import { ApiError } from '@eneco/dc/client/ApiError';
import { emptySuffixString } from '@eneco/hooks/address';
import { DC_Domain_Models_Products_ProductType, Usages_Reading_ResponseModel } from '@monorepo-types/dc';
import { useMachine } from '@xstate/react';
import { assign, setup } from 'xstate';

import { actors } from './MeterReadingsFlow.actors';
import {
  GetReadingErrorCode,
  saveReadingConfirmationTargets,
  SaveReadingErrorCode,
  saveReadingErrorTargets,
} from './MeterReadingsFlow.const';
import {
  addStep,
  hasCoolingMeter,
  hasElectricityMeter,
  hasGasMeter,
  hasTapwaterMeter,
  hasWarmthMeter,
  isMissingCoolingMeter,
  isMissingElectricityMeter,
  isMissingGasMeter,
  isMissingTapWaterMeter,
  isMissingWarmthMeter,
} from './MeterReadingsFlow.helpers';
import {
  Events,
  FUNNEL_START_ACTION_PARAMS,
  GetMeterReadingFlowMachineParams,
  ReadingContext,
  ReadingMeterModel,
  SAVE_ERRORS_ACTION_PARAMS,
  SET_READING_RESPONSE_ACTION_PARAMS,
  SET_SAVE_RESPONSE_ACTION_PARAMS,
  SHOULD_FETCH_READING_GUARD_PARAMS,
  StepValue,
  UPDATE_SAVE_READING_ACTION_PARAMS,
} from './MeterReadingsFlow.types';

type MeterReadingFlowMachineActorReturnArray = ReturnType<
  typeof useMachine<ReturnType<typeof getMeterReadingFlowMachine>>
>;

export type MeterReadingsFlowMachineContext = {
  state: MeterReadingFlowMachineActorReturnArray[0];
  send: MeterReadingFlowMachineActorReturnArray[1];
};

export const MeterReadingsFlowMachine = createContext({} as MeterReadingsFlowMachineContext);

export const useMeterReadingsFlow = () => {
  const context = useContext(MeterReadingsFlowMachine);
  if (!context) throw new Error('useMeterReadingFlow must be wrapped inside MeterReadingFlowMachineProvider');
  return context;
};

export const getMeterReadingFlowMachine = ({
  authenticationContext,
  readingId = null,
  houseNumber = null,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: GetMeterReadingFlowMachineParams): any =>
  setup({
    types: {
      context: {} as ReadingContext,
      events: {} as Events,
    },
    actors,
    actions: {
      SET_READING_RESPONSE: assign(({ context }, response: SET_READING_RESPONSE_ACTION_PARAMS) => {
        if (!response) return {};

        const { houseNumberSuffix } = response.address ?? {};
        const suffixPart = houseNumberSuffix && houseNumberSuffix !== emptySuffixString ? ` ${houseNumberSuffix}` : '';
        const { street, houseNumber: addressHouseNumber, postalCode, city } = response.address ?? {};

        return {
          reading: response,
          readingId: response.readingId,
          meters:
            response.meters?.reduce(
              (meters, meter) => ({
                ...meters,
                [meter.productType]: {
                  ...meter,
                  counters: meter?.counters?.map(counter => ({
                    ...counter,
                    value: 0,
                  })),
                },
              }),
              {} as NonNullable<ReadingContext['meters']>,
            ) ?? context.meters,
          addressLine: `${street} ${addressHouseNumber}${suffixPart}, ${postalCode} ${city}`,
        };
      }),
      UPDATE_SAVE_READING: assign(
        (
          { context },
          { readingId, houseNumber, meters, email, phoneNumber, mobilePhoneNumber }: UPDATE_SAVE_READING_ACTION_PARAMS,
        ) => {
          let metersValue = context.meters;

          if (meters) {
            const meterUpdates = Object.entries(meters) as [DC_Domain_Models_Products_ProductType, ReadingMeterModel][];
            metersValue = meterUpdates.reduce(
              (meters, [name, meter]) => ({
                ...meters,
                [name]: {
                  ...meters[name],
                  ...meter,
                },
              }),
              { ...context.meters },
            );
          }

          return {
            readingId: readingId || context.readingId,
            houseNumber: houseNumber || context.houseNumber,
            meters: metersValue,
            email: email || context.email,
            phoneNumber: phoneNumber || context.phoneNumber,
            mobilePhoneNumber: mobilePhoneNumber || context.mobilePhoneNumber,
          };
        },
      ),
      SET_SAVE_RESPONSE: assign({
        save: (_, response: SET_SAVE_RESPONSE_ACTION_PARAMS) => response ?? null,
      }),
      ADD_READING_ID_STEP: assign({
        steps: ({ context }) =>
          addStep({
            state: 'READING_ID',
          })(context),
      }),
      ADD_INTRODUCTION_STEP: assign({
        steps: ({ context }) =>
          addStep(
            {
              state: 'INTRODUCTION',
            },
            context.steps.findIndex(({ state }) => state === 'READING_ID') + 1,
          )(context),
      }),
      FUNNEL_START: assign({
        funnelStarted: () => true,
        funnelName: (_, funnelName: FUNNEL_START_ACTION_PARAMS) => funnelName,
      }),
      SAVE_ERRORS: assign({
        errors: ({ context, event }, meters: SAVE_ERRORS_ACTION_PARAMS) => {
          const newErrors = context.errors ? [...context.errors] : [];

          meters?.forEach(meter => {
            if (!meter.saveable) {
              newErrors.push({
                errorType: 'NOT_SAVEABLE_METER',
                errorContext: meter,
              });
            }
          });

          return newErrors;
        },
      }),
    },
    guards: {
      HAS_ELECTRICITY_METER: ({ context }) => hasElectricityMeter(context.meters),
      IS_MISSING_ELECTRICITY_METER: ({ context }) => isMissingElectricityMeter(context.meters),
      HAS_GAS_METER: ({ context }) => hasGasMeter(context.meters),
      IS_MISSING_GAS_METER: ({ context }) => isMissingGasMeter(context.meters),
      HAS_WARMTH_METER: ({ context }) => hasWarmthMeter(context.meters),
      IS_MISSING_WARMTH_METER: ({ context }) => isMissingWarmthMeter(context.meters),
      HAS_COOLING_METER: ({ context }) => hasCoolingMeter(context.meters),
      IS_MISSING_COOLING_METER: ({ context }) => isMissingCoolingMeter(context.meters),
      HAS_TAPWATER_METER: ({ context }) => hasTapwaterMeter(context.meters),
      IS_MISSING_TAPWATER_METER: ({ context }) => isMissingTapWaterMeter(context.meters),
      IS_NOT_SAVEABLE: (_, response?: Usages_Reading_ResponseModel) => {
        if (!response) return true;

        const { meters } = response;
        return !!meters && Object.values(meters).some(meter => !meter.saveable);
      },
      IS_PUBLIC: ({ context }) => context.accountId == null || !context.isAuthenticated,
      HAS_READING_ID_AND_HOUSE_NUMBER: ({ context }) => context.readingId !== null && context.houseNumber !== null,
      SHOULD_FETCH_READING: ({ context }, { readingId, houseNumber }: SHOULD_FETCH_READING_GUARD_PARAMS) => {
        return context.readingId !== readingId || context.houseNumber !== houseNumber || !context.reading;
      },
      IS_EXPECTED_GET_READING_ERROR: (
        _,
        { expectedErrorCode, actualError }: { expectedErrorCode: GetReadingErrorCode; actualError: unknown },
      ) => {
        if (!(actualError instanceof ApiError)) return false;

        return actualError.firstError?.code === expectedErrorCode;
      },
      IS_EXPECTED_SAVE_READING_ERROR: (
        _,
        { expectedErrorCode, actualError }: { expectedErrorCode: SaveReadingErrorCode; actualError: unknown },
      ) => {
        if (!(actualError instanceof ApiError)) return false;

        return actualError.firstError?.code === expectedErrorCode;
      },
      HAS_STEP_WITH_STATE: ({ context }, { state }: StepValue) => context.steps.some(step => step.state === state),
      IS_NETWORK_ERROR: (_, { error }: { error: unknown }) => {
        if (!(error instanceof ApiError)) return false;

        return !error.firstError;
      },
    },
  }).createMachine({
    id: 'METER_READINGS_FLOW',
    context: ({ input }) => ({
      ...authenticationContext,
      reading: null,
      readingId,
      houseNumber,
      meters: null,
      save: null,
      addressLine: null,
      email: '',
      steps: [
        {
          state: 'METER_READINGS',
        },
        {
          state: 'CONFIRMATION',
        },
      ],
      funnelStarted: false,
      errors: [],
      ...input,
    }),
    initial: 'READING_INTAKE',
    on: {
      FUNNEL_START: {
        actions: { type: 'FUNNEL_START', params: ({ event }) => event.funnelName },
      },
      GOTO_STEP: [
        {
          guard: ({ event }) => event.target === 'INTRODUCTION',
          target: '.INTRODUCTION',
        },
        {
          guard: ({ event }) => event.target === 'READING_ID',
          target: '.READING_ID',
        },
        {
          guard: ({ event }) => event.target === 'METER_READINGS',
          target: '.METER_READINGS',
        },
        {
          guard: ({ event }) => event.target === 'METER_READINGS.ELECTRICITY',
          target: '.METER_READINGS.ELECTRICITY',
        },
        {
          guard: ({ event }) => event.target === 'METER_READINGS.GAS',
          target: '.METER_READINGS.GAS',
        },
        {
          guard: ({ event }) => event.target === 'METER_READINGS.WARMTH',
          target: '.METER_READINGS.WARMTH',
        },
        {
          guard: ({ event }) => event.target === 'METER_READINGS.COOLING',
          target: '.METER_READINGS.COOLING',
        },
        {
          guard: ({ event }) => event.target === 'METER_READINGS.TAPWATER',
          target: '.METER_READINGS.TAPWATER',
        },
        {
          guard: ({ event }) => event.target === 'CONFIRMATION',
          target: '.CONFIRMATION',
        },
      ],
    },
    states: {
      READING_INTAKE: {
        initial: 'loading',
        always: [
          {
            guard: 'HAS_READING_ID_AND_HOUSE_NUMBER',
            target: 'READING_ID.LOADING',
            actions: 'ADD_READING_ID_STEP',
          },
          {
            guard: 'IS_PUBLIC',
            target: 'READING_ID',
            actions: 'ADD_READING_ID_STEP',
          },
        ],
        invoke: {
          id: 'GET_READING',
          src: 'GET_READING',
          input: ({ context }) => context,
          onDone: [
            {
              guard: { type: 'IS_NOT_SAVEABLE', params: ({ event }) => event.output },
              actions: [
                { type: 'SET_READING_RESPONSE', params: ({ event }) => event.output },
                { type: 'SAVE_ERRORS', params: ({ event }) => event.output?.meters },
              ],
              target: 'ERROR_PAGE.NotSaveableMeter',
            },
            {
              target: 'INTRODUCTION',
              actions: { type: 'SET_READING_RESPONSE', params: ({ event }) => event.output },
            },
          ],
          onError: {
            target: '.error',
          },
        },
        states: {
          loading: {},
          error: {},
        },
      },
      READING_ID: {
        initial: 'PENDING',
        on: {
          NEXT: [
            {
              target: '.LOADING',
              guard: {
                type: 'SHOULD_FETCH_READING',
                params: ({ event }) => event,
              },
              actions: {
                type: 'UPDATE_SAVE_READING',
                params: ({ event }) => event,
              },
            },
            {
              target: '.DONE',
            },
          ],
        },
        states: {
          LOADING: {
            invoke: {
              id: 'GET_READING_BY_READING_ID',
              src: 'GET_READING_BY_READING_ID',
              input: ({ context }) => context,
              onDone: [
                {
                  guard: { type: 'IS_NOT_SAVEABLE', params: ({ event }) => event.output },
                  actions: [
                    { type: 'SET_READING_RESPONSE', params: ({ event }) => event.output },
                    { type: 'SAVE_ERRORS', params: ({ event }) => event.output?.meters },
                  ],
                  target: '#METER_READINGS_FLOW.ERROR_PAGE.NotSaveableMeter',
                },
                {
                  target: 'DONE',
                  actions: { type: 'SET_READING_RESPONSE', params: ({ event }) => event.output },
                },
              ],
              onError: [
                {
                  target: 'ERROR_COMBINATION_EXPIRED',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.COMBINATION_EXPIRED,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_YEARNOTE',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.MVS_ERROR_YEARNOTE,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_ENDNOTE',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.MVS_ERROR_ENDNOTE,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_SWITCH',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.MVS_ERROR_SWITCH,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_DATA_ALREADY_SET',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.DATA_ALREADY_SET,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_INVALID_COMBINATION',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.INVALID_COMBINATION,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_READING_ID_UNKNOWN',
                  guard: {
                    type: 'IS_EXPECTED_GET_READING_ERROR',
                    params: ({ event }) => ({
                      expectedErrorCode: GetReadingErrorCode.READING_ID_UKNOWN,
                      actualError: event.error,
                    }),
                  },
                },
                {
                  target: 'ERROR_UNKNOWN',
                },
              ],
            },
          },
          PENDING: {},
          ERROR_YEARNOTE: {},
          ERROR_ENDNOTE: {},
          ERROR_SWITCH: {},
          ERROR_COMBINATION_EXPIRED: {},
          ERROR_INVALID_COMBINATION: {},
          ERROR_UNKNOWN: {},
          ERROR_READING_ID_UNKNOWN: {},
          ERROR_DATA_ALREADY_SET: {},
          DONE: {
            type: 'final',
          },
        },
        onDone: 'INTRODUCTION',
      },
      INTRODUCTION: {
        id: 'INTRODUCTION',
        on: {
          NEXT: 'METER_READINGS',
          PREVIOUS_STEP: [
            {
              target: '#METER_READINGS_FLOW.READING_ID',
              guard: { type: 'HAS_STEP_WITH_STATE', params: { state: 'READING_ID' } },
            },
          ],
        },
        initial: 'PENDING',
        states: {
          PENDING: {
            always: [
              {
                guard: ({ context }) => context?.reading?.readingType === 'PER',
                actions: ['ADD_INTRODUCTION_STEP'],
                target: 'YEARNOTE',
              },
              {
                guard: ({ context }) => context?.reading?.readingType === 'EOS',
                actions: ['ADD_INTRODUCTION_STEP'],
                target: 'ENDNOTE',
              },
              {
                guard: ({ context }) => context?.reading?.readingType === 'TME',
                actions: ['ADD_INTRODUCTION_STEP'],
                target: 'TME',
              },
              {
                guard: ({ context }) => context?.reading?.readingType === 'ER',
                actions: ['ADD_INTRODUCTION_STEP'],
                target: 'ENERGY_REPORT',
              },
              {
                target: '#METER_READINGS_FLOW.METER_READINGS',
              },
            ],
          },
          YEARNOTE: {},
          ENDNOTE: {},
          TME: {},
          ENERGY_REPORT: {},
        },
      },
      METER_READINGS: {
        initial: 'ELECTRICITY',
        on: {
          PREVIOUS_STEP: [
            {
              target: '#METER_READINGS_FLOW.INTRODUCTION',
              guard: { type: 'HAS_STEP_WITH_STATE', params: { state: 'INTRODUCTION' } },
            },
            {
              target: '#METER_READINGS_FLOW.READING_ID',
              guard: { type: 'HAS_STEP_WITH_STATE', params: { state: 'READING_ID' } },
            },
          ],
        },
        states: {
          ELECTRICITY: {
            always: [
              {
                guard: 'IS_MISSING_ELECTRICITY_METER',
                target: 'GAS',
              },
            ],
            on: {
              NEXT: {
                actions: {
                  type: 'UPDATE_SAVE_READING',
                  params: ({ event }) => event,
                },
                target: 'GAS',
              },
            },
          },
          GAS: {
            always: [
              {
                guard: 'IS_MISSING_GAS_METER',
                target: 'WARMTH',
              },
            ],
            on: {
              NEXT: {
                actions: {
                  type: 'UPDATE_SAVE_READING',
                  params: ({ event }) => event,
                },
                target: 'WARMTH',
              },
              PREVIOUS_STEP: [
                {
                  guard: 'HAS_ELECTRICITY_METER',
                  target: 'ELECTRICITY',
                },
              ],
            },
          },
          WARMTH: {
            always: [
              {
                guard: 'IS_MISSING_WARMTH_METER',
                target: 'COOLING',
              },
            ],
            on: {
              NEXT: {
                actions: {
                  type: 'UPDATE_SAVE_READING',
                  params: ({ event }) => event,
                },
                target: 'COOLING',
              },
              PREVIOUS_STEP: [
                {
                  guard: 'HAS_GAS_METER',
                  target: 'GAS',
                },
                {
                  guard: 'HAS_ELECTRICITY_METER',
                  target: 'ELECTRICITY',
                },
              ],
            },
          },
          COOLING: {
            always: [
              {
                guard: 'IS_MISSING_COOLING_METER',
                target: 'TAPWATER',
              },
            ],
            on: {
              NEXT: {
                actions: {
                  type: 'UPDATE_SAVE_READING',
                  params: ({ event }) => event,
                },
                target: 'TAPWATER',
              },
              PREVIOUS_STEP: [
                {
                  guard: 'HAS_WARMTH_METER',
                  target: 'WARMTH',
                },
                {
                  guard: 'HAS_GAS_METER',
                  target: 'GAS',
                },
                {
                  guard: 'HAS_ELECTRICITY_METER',
                  target: 'ELECTRICITY',
                },
              ],
            },
          },
          TAPWATER: {
            always: [
              {
                guard: 'IS_MISSING_TAPWATER_METER',
                target: 'DONE',
              },
            ],
            on: {
              NEXT: {
                actions: {
                  type: 'UPDATE_SAVE_READING',
                  params: ({ event }) => event,
                },
                target: 'DONE',
              },
              PREVIOUS_STEP: [
                {
                  guard: 'HAS_COOLING_METER',
                  target: 'COOLING',
                },
                {
                  guard: 'HAS_WARMTH_METER',
                  target: 'WARMTH',
                },
                {
                  guard: 'HAS_GAS_METER',
                  target: 'GAS',
                },
                {
                  guard: 'HAS_ELECTRICITY_METER',
                  target: 'ELECTRICITY',
                },
              ],
            },
          },
          DONE: {
            type: 'final',
          },
        },
        onDone: 'CONFIRMATION',
      },
      CONFIRMATION: {
        initial: 'PENDING',
        on: {
          SEND: [
            {
              actions: {
                type: 'UPDATE_SAVE_READING',
                params: ({ event }) => event,
              },
              target: '.LOADING.PUBLIC',
              guard: 'IS_PUBLIC',
            },
            {
              actions: {
                type: 'UPDATE_SAVE_READING',
                params: ({ event }) => event,
              },
              target: '.LOADING.PRIVATE.SAVE_EMAIL',
              guard: ({ event }) => !!event.email,
            },
            {
              actions: {
                type: 'UPDATE_SAVE_READING',
                params: ({ event }) => event,
              },
              target: '.LOADING.PRIVATE.SAVE',
            },
          ],
          PREVIOUS_STEP: [
            {
              guard: 'HAS_TAPWATER_METER',
              target: 'METER_READINGS.TAPWATER',
            },
            {
              guard: 'HAS_COOLING_METER',
              target: 'METER_READINGS.COOLING',
            },
            {
              guard: 'HAS_WARMTH_METER',
              target: 'METER_READINGS.WARMTH',
            },
            {
              guard: 'HAS_GAS_METER',
              target: 'METER_READINGS.GAS',
            },
            {
              guard: 'HAS_ELECTRICITY_METER',
              target: 'METER_READINGS.ELECTRICITY',
            },
          ],
        },
        states: {
          LOADING: {
            initial: 'INITIAL',
            states: {
              INITIAL: {},
              PUBLIC: {
                invoke: {
                  id: 'SAVE_READING_BY_READING_ID',
                  src: 'SAVE_READING_BY_READING_ID',
                  input: ({ context }) => context,
                  onDone: saveReadingConfirmationTargets,
                  onError: saveReadingErrorTargets,
                },
              },
              PRIVATE: {
                initial: 'INITIAL',
                states: {
                  INITIAL: {},
                  SAVE_EMAIL: {
                    invoke: {
                      id: 'SAVE_EMAIL',
                      src: 'SAVE_EMAIL',
                      input: ({ context }) => context,
                      onDone: {
                        target: '#METER_READINGS_FLOW.CONFIRMATION.LOADING.PRIVATE.SAVE',
                      },
                      onError: saveReadingErrorTargets,
                    },
                  },
                  SAVE: {
                    invoke: {
                      id: 'SAVE_READING',
                      src: 'SAVE_READING',
                      input: ({ context }) => context,
                      onDone: saveReadingConfirmationTargets,
                      onError: saveReadingErrorTargets,
                    },
                  },
                },
              },
            },
          },
          PENDING: {},
          ERROR_YEARNOTE: {},
          ERROR_SWITCH: {},
          ERROR_ENDNOTE: {},
          ERROR_UNKNOWN: {},
          ERROR_NETWORK: {},
        },
      },
      THANK_YOU: {
        initial: 'Initial',
        states: {
          Initial: {},
          Adhoc: { type: 'final' },
          TariffMigration: { type: 'final' },
          CloseDown: { type: 'final' },
          PeriodicDelayed: { type: 'final' },
          PeriodicEven: { type: 'final' },
          PeriodicNewTermAmount: { type: 'final' },
          PeriodicPayBack: { type: 'final' },
          PeriodicPayBackMore: { type: 'final' },
          PeriodicPayBackLess: { type: 'final' },
          PeriodicPayBackTermAmount: { type: 'final' },
          PeriodicGetBackTermAmount: { type: 'final' },
          PeriodicGetBack: { type: 'final' },
          Registration: { type: 'final' },
          RelocationNewAddress: { type: 'final' },
          RelocationOldAddress: { type: 'final' },
          EnergyReport: { type: 'final' },
          Unknown: { type: 'final' },
        },
      },
      ERROR_PAGE: {
        initial: 'Standard',
        states: {
          Standard: { type: 'final' },
          NotSaveableMeter: { type: 'final' },
        },
      },
    },
  });
